import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trophy, Users, Zap, Shield, Link as LinkIcon, SlidersHorizontal,
  Target, MessageSquare, ChevronDown, ChevronUp, ArrowRight,
  Check, Clock, Share2, BarChart3, Award, Tv, TrendingUp, Star
} from 'lucide-react';

/* ──────────────── COUNTDOWN TIMER ──────────────── */
function SeasonCountdown() {
  const targetDate = new Date('2026-08-22T12:00:00Z');
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff <= 0) { setTimeLeft(null); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, []);

  if (!timeLeft) return null;

  return (
    <div className="bg-gradient-to-r from-[#5b3cc4] to-[#6366f1] rounded-xl px-4 py-3 text-white">
      <p className="text-white/80 text-xs font-medium mb-2 text-center">2026/27 EPL Season Starts</p>
      <div className="flex justify-center gap-4">
        {[
          { val: timeLeft.days, label: 'Days' },
          { val: timeLeft.hours, label: 'Hrs' },
          { val: timeLeft.minutes, label: 'Min' },
          { val: timeLeft.seconds, label: 'Sec' },
        ].map(t => (
          <div key={t.label} className="text-center">
            <div className="font-heading text-xl font-bold tabular-nums">{String(t.val).padStart(2, '0')}</div>
            <div className="text-white/50 text-[9px]">{t.label}</div>
          </div>
        ))}
      </div>
      <p className="text-white/50 text-[10px] mt-1.5 text-center">August 22, 2026</p>
    </div>
  );
}

/* ──────────────── LEADERBOARD PREVIEW ──────────────── */
function LeaderboardPreview() {
  const rows = [
    { rank: 1, name: 'DOMO TEMPLE BAR', pts: 495, t1: 'Arsenal', color: 'bg-yellow-400' },
    { rank: 2, name: 'LAURENCE MCQUARRIE', pts: 480, t1: 'Chelsea', color: 'bg-gray-300' },
    { rank: 3, name: 'PHILIP ZITA', pts: 451, t1: 'Liverpool', color: 'bg-amber-600' },
  ];
  return (
    <div className="relative">
      <div className="absolute -top-3 -right-3 z-10">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#5b3cc4] text-white px-3 py-1 text-xs font-medium shadow-lg">
          <span className="h-1.5 w-1.5 rounded-full bg-purple-300 animate-pulse" />Auto-scoring
        </span>
      </div>
      <div className="rounded-2xl border bg-white shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b">
          <div className="flex gap-1.5"><div className="h-3 w-3 rounded-full bg-red-400" /><div className="h-3 w-3 rounded-full bg-yellow-400" /><div className="h-3 w-3 rounded-full bg-green-400" /></div>
          <div className="flex-1 text-center"><span className="text-xs text-gray-400 font-mono">poolsfc.co/standings</span></div>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-base">EPL Pool 2026/27 Standings</h3>
            <Badge className="bg-green-100 text-green-700 text-[10px]">LIVE</Badge>
          </div>
          <div className="space-y-2.5">
            {rows.map(r => (
              <div key={r.rank} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${r.rank === 1 ? 'bg-[#f3f0ff]/50 ring-1 ring-[#5b3cc4]/20' : 'bg-gray-50'}`}>
                <div className={`h-8 w-8 rounded-full ${r.color} flex items-center justify-center`}><span className="text-xs font-bold text-white">{r.rank}</span></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground">{r.t1} + 4 more</p>
                </div>
                <span className="font-heading font-bold text-lg tabular-nums text-[#5b3cc4]">{r.pts}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[{ val: '319', label: 'Entrants' }, { val: 'Week 30', label: 'Current' }, { val: '7,975', label: 'Prize Pool' }].map(s => (
              <div key={s.label} className="text-center rounded-xl bg-[#f3f0ff] py-2.5">
                <p className="font-heading text-base font-bold text-[#5b3cc4]">{s.val}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── MATCH CENTRE SHOWCASE ──────────────── */
function MatchCentreShowcase() {
  return (
    <div className="space-y-6">
      {/* Full-width Standings Illustration */}
      <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
          <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
          <span className="text-sm font-semibold text-gray-600 ml-2">Pool Standings</span>
          <Badge className="ml-auto bg-green-100 text-green-700 text-[9px]">LIVE</Badge>
        </div>
        <div className="p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground w-10">#</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Team Name</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[#5b3cc4] font-bold">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Name</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Tier 1</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Tier 2</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Tier 3</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Tier 4</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Tier 5</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Goals</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { rank: 1, alias: 'DOMO TEMPLE BAR', pts: 495, name: 'Dom K.', t1: { team: 'Arsenal', pts: 47 }, t2: { team: 'Aston Villa', pts: 72 }, t3: { team: 'C. Palace', pts: 78 }, t4: { team: 'Man Utd', pts: 148 }, t5: { team: 'Sunderland', pts: 150 }, goals: 1050, highlight: true },
                  { rank: 2, alias: 'THE GUNNERS FC', pts: 480, name: 'Sarah M.', t1: { team: 'Arsenal', pts: 47 }, t2: { team: 'Newcastle', pts: 56 }, t3: { team: 'Brentford', pts: 93 }, t4: { team: 'Man Utd', pts: 148 }, t5: { team: 'Sunderland', pts: 150 }, goals: 1020 },
                  { rank: 3, alias: 'VILLA BOYS', pts: 451, name: 'Kyle R.', t1: { team: 'Liverpool', pts: 34 }, t2: { team: 'Aston Villa', pts: 72 }, t3: { team: 'Fulham', pts: 84 }, t4: { team: 'Everton', pts: 124 }, t5: { team: 'Leeds', pts: 115 }, goals: 1080 },
                  { rank: 4, alias: 'PURPLE REIGN', pts: 435, name: 'John D.', t1: { team: 'Chelsea', pts: 35 }, t2: { team: 'Brighton', pts: 60 }, t3: { team: 'Bournemouth', pts: 84 }, t4: { team: 'Man Utd', pts: 148 }, t5: { team: 'Tottenham', pts: 110 }, goals: 990 },
                  { rank: 5, alias: 'FOOTY KINGS', pts: 410, name: 'Dave P.', t1: { team: 'Man City', pts: 42 }, t2: { team: 'Newcastle', pts: 56 }, t3: { team: 'Brentford', pts: 93 }, t4: { team: 'West Ham', pts: 72 }, t5: { team: 'Sunderland', pts: 150 }, goals: 1100 },
                ].map(r => (
                  <tr key={r.rank} className={`border-b ${r.highlight ? 'bg-[#f3f0ff]/40' : 'hover:bg-gray-50'}`}>
                    <td className="px-3 py-2.5 font-mono font-bold text-sm">{r.rank}</td>
                    <td className="px-3 py-2.5 font-semibold text-sm">{r.alias}</td>
                    <td className="px-3 py-2.5 text-center font-heading font-bold text-lg text-[#5b3cc4]">{r.pts}</td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">{r.name}</td>
                    {[r.t1, r.t2, r.t3, r.t4, r.t5].map((t, i) => (
                      <td key={t.team} className="px-3 py-2.5 text-center">
                        <div className="text-[10px] text-muted-foreground">{t.team}</div>
                        <div className="font-mono text-sm font-bold">{t.pts}</div>
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-center font-mono text-sm">{r.goals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 pb-4"><p className="text-sm text-muted-foreground">Live standings with tier-by-tier breakdowns. Click any row to see week-by-week point accumulation. Sortable by any column.</p></div>
      </div>

      {/* Row 1: Fixtures + Team Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Fixtures */}
        <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
            <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
            <span className="text-sm font-semibold text-gray-600 ml-2">Live Fixtures & Scores</span>
            <Badge className="ml-auto bg-green-100 text-green-700 text-[9px]">LIVE</Badge>
          </div>
          <div className="p-5 space-y-3">
            {[
              { home: 'Arsenal', hScore: 2, away: 'Chelsea', aScore: 1, status: 'FT', badge: 'bg-gray-100 text-gray-600' },
              { home: 'Liverpool', hScore: 3, away: 'Man City', aScore: 0, status: 'FT', badge: 'bg-gray-100 text-gray-600' },
              { home: 'Aston Villa', hScore: 1, away: 'Newcastle', aScore: 1, status: 'FT', badge: 'bg-gray-100 text-gray-600' },
              { home: 'Brentford', hScore: null, away: 'Brighton', aScore: null, status: 'Sat 15:00', badge: 'bg-blue-50 text-blue-600' },
              { home: 'Everton', hScore: null, away: 'Fulham', aScore: null, status: 'Sat 17:30', badge: 'bg-blue-50 text-blue-600' },
            ].map((m, i) => (
              <div key={m.home} className="flex items-center justify-between rounded-xl bg-white p-3 border hover:border-[#5b3cc4]/30 transition-colors">
                <span className="font-medium text-sm w-28 text-right">{m.home}</span>
                <div className="text-center w-20">
                  {m.hScore !== null ? (
                    <div><span className="font-heading font-bold text-lg">{m.hScore} - {m.aScore}</span><div className="text-[10px] text-muted-foreground">FT</div></div>
                  ) : (
                    <Badge className={`text-[10px] ${m.badge}`}>{m.status}</Badge>
                  )}
                </div>
                <span className="font-medium text-sm w-28">{m.away}</span>
              </div>
            ))}
          </div>
          <div className="px-5 pb-4"><p className="text-sm text-muted-foreground">Real-time scores on matchdays. Browse by date, click any match for full details.</p></div>
        </div>

        {/* Team Statistics */}
        <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
            <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
            <span className="text-sm font-semibold text-gray-600 ml-2">Team Statistics</span>
          </div>
          <div className="p-5">
            <div className="rounded-xl overflow-hidden border">
              <div className="bg-gradient-to-r from-[#5b3cc4] to-[#6366f1] px-5 py-4 text-white flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center font-heading font-bold">ARS</div>
                <div className="flex-1">
                  <div className="text-lg font-bold">Arsenal</div>
                  <div className="text-white/70 text-xs">Tier 1 · Win 2x · Draw 1x</div>
                  <div className="flex gap-1 mt-1.5">{['W','W','D','W','W'].map((r,i) => <div key={`f-${i}`} className={`h-5 w-5 rounded text-[9px] font-bold flex items-center justify-center ${r==='W'?'bg-green-400 text-green-900':'bg-yellow-400 text-yellow-900'}`}>{r}</div>)}</div>
                </div>
                <div className="text-right"><div className="text-3xl font-heading font-bold">47</div><div className="text-white/70 text-[10px]">Pool Points</div></div>
              </div>
              <div className="grid grid-cols-4 divide-x border-b">
                {[{l:'Played',v:'30'},{l:'Won',v:'20',c:'text-green-600'},{l:'Drawn',v:'7',c:'text-yellow-600'},{l:'Lost',v:'3',c:'text-red-500'}].map(s => (
                  <div key={s.l} className="py-3 text-center"><div className={`font-heading text-xl font-bold ${s.c || ''}`}>{s.v}</div><div className="text-[10px] text-muted-foreground">{s.l}</div></div>
                ))}
              </div>
              <div className="grid grid-cols-4 divide-x">
                {[{l:'Goals For',v:'59'},{l:'Goals Agst',v:'22'},{l:'Clean Sheets',v:'14'},{l:'Win Streak',v:'5'}].map(s => (
                  <div key={s.l} className="py-3 text-center"><div className="font-heading text-lg font-bold">{s.v}</div><div className="text-[10px] text-muted-foreground">{s.l}</div></div>
                ))}
              </div>
              <div className="px-4 py-2.5 bg-[#f3f0ff]/50 text-xs text-muted-foreground">20 wins × 2 + 7 draws × 1 = <strong className="text-[#5b3cc4]">47 pool points</strong></div>
            </div>
          </div>
          <div className="px-5 pb-4"><p className="text-sm text-muted-foreground">Click any of the 20 EPL teams to see full season stats, streaks, goals by time, and pool point breakdown.</p></div>
        </div>
      </div>

      {/* Row 2: Top Players + Chat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Players */}
        <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
            <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
            <span className="text-sm font-semibold text-gray-600 ml-2">Top Scorers & Assists</span>
          </div>
          <div className="p-5 space-y-2">
            {[
              { rank: 1, name: 'E. Haaland', team: 'Man City', stat: 22, sub: '28 apps · 81 shots · 7.4 rating' },
              { rank: 2, name: 'Thiago', team: 'Brentford', stat: 18, sub: '29 apps · 64 shots · 7.1 rating' },
              { rank: 3, name: 'João Pedro', team: 'Chelsea', stat: 14, sub: '27 apps · 52 shots · 6.9 rating' },
              { rank: 4, name: 'H. Ekitike', team: 'Liverpool', stat: 11, sub: '25 apps · 41 shots · 6.8 rating' },
            ].map(p => (
              <div key={p.rank} className="flex items-center gap-3 rounded-xl bg-white p-3 border hover:border-[#5b3cc4]/30 transition-colors">
                <span className="font-mono font-bold text-muted-foreground w-6 text-center">{p.rank}</span>
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#5b3cc4] to-[#6366f1] flex items-center justify-center"><Star className="h-4 w-4 text-white" /></div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{p.team} · {p.sub}</div>
                </div>
                <div className="text-right"><span className="font-heading font-bold text-xl text-[#5b3cc4]">{p.stat}</span><div className="text-[9px] text-muted-foreground">goals</div></div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-4"><p className="text-sm text-muted-foreground">Top 20 scorers and assisters with expandable player profiles — appearances, shots, passes, tackles, and more.</p></div>
        </div>

        {/* Pool Chat */}
        <div className="rounded-2xl border bg-[#fafafa] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 px-5 py-3 bg-white border-b">
            <div className="flex gap-1.5"><div className="h-2.5 w-2.5 rounded-full bg-red-400" /><div className="h-2.5 w-2.5 rounded-full bg-yellow-400" /><div className="h-2.5 w-2.5 rounded-full bg-green-400" /></div>
            <span className="text-sm font-semibold text-gray-600 ml-2">Pool Chat</span>
          </div>
          <div className="p-5 space-y-3">
            {[
              { name: 'John C.', msg: 'Arsenal just scored! That\'s +2 for Tier 1 picks 🔥', time: '2m', role: 'Organizer' },
              { name: 'Sarah M.', msg: 'My Sunderland pick has been incredible this season. Tier 5 gang rise up!', time: '5m' },
              { name: 'Kyle R.', msg: 'Who\'s got their goals prediction closest? I said 1,050 — looking good so far.', time: '12m' },
              { name: 'Dave P.', msg: 'Man United Tier 4 was the play this year. 148 pool points from one pick!', time: '18m' },
            ].map((m, i) => (
              <div key={m.name} className="flex gap-3 rounded-xl bg-white p-3 border">
                <div className="h-8 w-8 rounded-full bg-[#5b3cc4] flex-shrink-0 flex items-center justify-center"><span className="text-[10px] font-bold text-white">{m.name[0]}</span></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold">{m.name}</span>
                    {m.role && <Badge className="bg-[#f3f0ff] text-[#5b3cc4] text-[8px] h-4">{m.role}</Badge>}
                    <span className="text-[10px] text-muted-foreground ml-auto">{m.time}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.msg}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-4"><p className="text-sm text-muted-foreground">Real-time pool discussions. Celebrate wins, debate picks, and track the action together all season.</p></div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── FAQ ──────────────── */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'How does the tier scoring system work?', a: <>Teams are divided into 5 tiers based on the previous season's standings. Each tier has a win and draw multiplier. Your total score is the sum of pool points from your 5 selected teams across the season. <a href="#tier-scoring" className="text-[#5b3cc4] hover:underline">See tier details above</a>.</> },
    { q: 'How many entries can I have per pool?', a: 'Each person can have up to 2 entries per pool. Each entry requires picking one team from each of the 5 tiers. You must also predict total season goals — it\'s used as a tiebreaker.' },
    { q: 'Is this gambling?', a: 'No. Pools FC is a free software platform for managing prediction pools. We do not collect entry fees, process wagers, or distribute prize money. Any pool fees and prizes are managed entirely by the pool organizer outside the platform.' },
    { q: 'Can I run my own pool?', a: <>Absolutely. Any registered user can create a pool and invite others. <Link to="/plans" className="text-[#5b3cc4] hover:underline">View plans and get started</Link>.</> },
    { q: 'Does it cost anything?', a: 'No. Pools FC is completely free for participants. Pool organizers can select from paid plans to enhance the user experience.' },
    { q: 'What\'s included in paid plans?', a: 'All paid plans include full access to Match Centre (live fixtures, league table, team statistics, top players), pool chat, and everything in the free tier. The difference between paid plans is the number of entrants allowed.' },
    { q: 'What leagues are supported?', a: 'We currently support the English Premier League (EPL). The platform is built for expansion to La Liga, Serie A, Bundesliga, and more leagues in future seasons.' },
    { q: 'How are prizes handled?', a: 'Prize percentages are set by the pool organizer (default: 1st 45%, 2nd 25%, 3rd 15%, Middle 10%, Goals 5%). The platform calculates who wins — the organizer distributes prizes offline.' },
  ];

  // FAQ Schema for SEO - injected safely via useEffect
  const faqSchemaText = faqs.map(f => ({
    q: typeof f.q === 'string' ? f.q : String(f.q),
    a: typeof f.a === 'string' ? f.a : 'See our website for details.',
  }));

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqSchemaText.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.id = 'faq-schema';
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => { const el = document.getElementById('faq-schema'); if (el) el.remove(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-3xl mx-auto space-y-3">
      {faqs.map((faq, i) => (
        <div key={faq.q} className="rounded-xl border bg-white overflow-hidden" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
          <button className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50" onClick={() => setOpenIndex(openIndex === i ? null : i)}>
            <span className="text-sm font-semibold pr-4" itemProp="name">{faq.q}</span>
            {openIndex === i ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          </button>
          {openIndex === i && <div className="px-4 pb-4" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"><p className="text-sm text-muted-foreground leading-relaxed" itemProp="text">{faq.a}</p></div>}
        </div>
      ))}
    </div>
  );
}

/* ──────────────── MAIN LANDING PAGE ──────────────── */
export default function LandingPage() {
  const [howItWorksTab, setHowItWorksTab] = useState('organizer');
  useEffect(() => { document.title = "Pools FC — Free Premier League Prediction Pool Manager"; }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex h-[64px] sm:h-[120px] items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <img src="/poolsfc-logo.png" alt="Pools FC Premier League Pool Manager Logo" className="h-[48px] w-[48px] sm:h-[104px] sm:w-[104px] rounded-lg" />
            <span>Pools FC</span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground">Sign In</Link>
            <Button asChild size="sm" className="bg-[#5b3cc4] hover:bg-[#4e33a8]"><Link to="/plans">Get Started Free</Link></Button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium">Premier League</Badge>
                <Badge variant="outline" className="font-medium">2026/27 Season</Badge>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.1] mb-5">
                Your Premier League pool <span className="text-[#5b3cc4]">headquarters</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                Create your own EPL prediction pool or join an existing one. Pick teams across 5 tiers, track real-time standings, and compete with friends. Free for entrants.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Button asChild size="lg" className="bg-[#5b3cc4] hover:bg-[#4e33a8] text-base px-6"><Link to="/plans">Start a Pool <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
                <Button asChild size="lg" variant="outline" className="text-base px-6"><Link to="/browse"><Users className="h-4 w-4 mr-1" /> Join a Pool</Link></Button>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
                <div className="flex items-center gap-3 border-r pr-6"><span className="font-heading text-2xl font-bold">20</span><span className="text-muted-foreground leading-tight text-xs">EPL<br/>Teams</span></div>
                <div className="flex items-center gap-3 border-r pr-6"><span className="font-heading text-2xl font-bold">5</span><span className="text-muted-foreground leading-tight text-xs">Scoring<br/>Tiers</span></div>
                <div className="flex items-center gap-3"><span className="font-heading text-2xl font-bold">Free</span><span className="text-muted-foreground leading-tight text-xs">For<br/>Entrants</span></div>
              </div>

              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#f3f0ff] border border-[#5b3cc4]/20 px-4 py-2">
                <Trophy className="h-4 w-4 text-[#5b3cc4]" />
                <span className="text-sm font-semibold text-[#5b3cc4]">Tiered Scoring Pool Model</span>
              </div>

              <div className="mt-4">
                <SeasonCountdown />
              </div>
            </div>
            <div className="hidden lg:block"><LeaderboardPreview /></div>
          </div>
        </div>
      </section>

      {/* PAIN POINT / UPGRADE SECTION */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-4">WHY POOLS FC</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your Office Pool Just Got a Major Upgrade</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-lg leading-relaxed">Stop wrestling with spreadsheets, manually updating scores, and sending email updates. Pools FC automates your English Premier League pool from start to finish.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* The Problem */}
            <div className="rounded-2xl border border-red-200/60 bg-gradient-to-b from-red-50/50 to-white p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <span className="text-red-500 text-lg font-bold">✕</span>
                </div>
                <h3 className="font-heading text-xl font-bold text-red-700">Spreadsheets Were Never Built for This</h3>
              </div>
              <p className="text-muted-foreground mb-6 leading-relaxed">You know the drill. Someone volunteers to run the office pool and suddenly they're buried in:</p>
              <ul className="space-y-4">
                {[
                  'Spreadsheets that break every time someone edits the wrong cell',
                  'Chasing people for payments via text, email, and awkward hallway conversations',
                  'Manually updating scores every weekend',
                  'Copy-pasting standings into group emails that half the pool never reads',
                  'Figuring out tiebreakers with a calculator and a prayer',
                ].map((item, i) => (
                  <li key={item.slice(0, 20)} className="flex items-start gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5"><span className="text-red-500 text-xs font-bold">✕</span></span>
                    <span className="text-sm text-foreground/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-red-400 mt-6 italic font-medium">It works — barely. Until it doesn't.</p>
            </div>

            {/* The Solution */}
            <div className="rounded-2xl border border-[#5b3cc4]/20 bg-gradient-to-b from-[#f3f0ff]/50 to-white p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-[#f3f0ff] flex items-center justify-center">
                  <Check className="h-5 w-5 text-[#5b3cc4]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#5b3cc4]">Pools FC Handles Everything</h3>
              </div>
              <p className="text-muted-foreground mb-6 leading-relaxed">Focus on the football. We'll handle the rest:</p>
              <ul className="space-y-4">
                {[
                  { title: 'Automated Scoring', desc: 'Match results sync from live data. Points calculated instantly.' },
                  { title: 'Live Standings', desc: 'Sortable leaderboard with week-by-week breakdowns. Always up to date.' },
                  { title: 'Easy Entry Management', desc: 'Entrants join online, pick their teams, and track their position.' },
                  { title: 'Built-in Chat', desc: 'Pool discussions happen in one place — not scattered across group texts.' },
                  { title: 'Fair Tiebreakers', desc: 'Goals prediction variance calculated automatically. No arguments.' },
                ].map((item, i) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#f3f0ff] flex items-center justify-center mt-0.5"><Check className="h-3.5 w-3.5 text-[#5b3cc4]" /></span>
                    <div>
                      <span className="text-sm font-semibold text-foreground">{item.title}</span>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Button asChild size="sm" className="bg-[#5b3cc4] hover:bg-[#4e33a8]">
                  <Link to="/plans">Start Your Pool <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MATCH CENTRE SHOWCASE */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">MATCH CENTRE</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your Pool Comes Alive on Matchdays</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Live scores, team deep dives, player stats, and pool chat — all inside your pool page. Included with all paid plans.</p>
          </div>
          <MatchCentreShowcase />
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">FEATURES</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Everything You Need to Run Your Premier League Pool</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Instant Setup', desc: 'Create your pool in seconds. Set prizes, configure tier multipliers, and share an invite link.' },
              { icon: BarChart3, title: 'Live Standings', desc: 'Standings update automatically. Sortable leaderboard with tier breakdowns and weekly accordion view.' },
              { icon: Shield, title: 'Fair & Transparent', desc: 'Tier-based picks enforced. Max 2 entries per person. Idempotent scoring engine ensures accuracy.' },
              { icon: LinkIcon, title: 'Easy Invites', desc: 'Share one invite link. Entrants join instantly — no complicated setup, just click and pick.' },
              { icon: SlidersHorizontal, title: 'Custom Scoring & Prizes', desc: 'Organizers control tier multipliers and prize percentages. Must total 100%.' },
              { icon: TrendingUp, title: 'Multi-League Ready', desc: 'Built for EPL, La Liga, Serie A, and Bundesliga. Select from 4 major European leagues.' },
            ].map(f => (
              <div key={f.title} className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow">
                <div className="h-10 w-10 rounded-xl bg-[#f3f0ff] flex items-center justify-center mb-4"><f.icon className="h-5 w-5 text-[#5b3cc4]" /></div>
                <h3 className="font-heading text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIER SCORING */}
      <section id="tier-scoring" className="py-16 sm:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">How Tier Scoring Works</h2>
            <p className="text-muted-foreground mt-3">Pick underdogs for bigger rewards. Lower tier = higher points.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-4xl mx-auto">
            {[
              { tier: 1, teams: 'Arsenal, Chelsea, Liverpool, Man City', win: 2, draw: 1, color: 'from-blue-500 to-blue-600' },
              { tier: 2, teams: 'Aston Villa, Brighton, Newcastle, Forest', win: 4, draw: 2, color: 'from-indigo-500 to-indigo-600' },
              { tier: 3, teams: 'Bournemouth, Brentford, Palace, Fulham', win: 6, draw: 3, color: 'from-violet-500 to-violet-600' },
              { tier: 4, teams: 'Everton, Man United, West Ham, Wolves', win: 8, draw: 4, color: 'from-purple-500 to-purple-600' },
              { tier: 5, teams: 'Burnley, Leeds, Sunderland, Tottenham', win: 10, draw: 5, color: 'from-fuchsia-500 to-fuchsia-600' },
            ].map(t => (
              <div key={t.tier} className="rounded-2xl border bg-white overflow-hidden hover:shadow-md transition-shadow">
                <div className={`bg-gradient-to-br ${t.color} px-4 py-3 text-white`}>
                  <p className="text-xs font-medium opacity-80">Tier {t.tier}</p>
                  <div className="flex items-baseline gap-2 mt-1"><span className="text-2xl font-heading font-bold">{t.win}x</span><span className="text-xs opacity-80">Win</span></div>
                  <p className="text-xs opacity-80">{t.draw}x Draw</p>
                </div>
                <div className="p-3"><p className="text-[11px] text-muted-foreground leading-relaxed">{t.teams}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">PRICING</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Simple, Transparent Pricing</h2>
            <p className="text-muted-foreground mt-3">Free for entrants. Organizers choose a plan based on pool size. All paid plans include full features.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-4xl mx-auto">
            {[
              { name: 'Free', price: 'Free', cap: 'Up to 8', features: ['Standings','Announcements','Rules'], popular: false },
              { name: 'Basic', price: '$47', cap: 'Up to 8', features: ['Everything in Free','Match Centre','Chat','Team & Player Stats'], popular: false },
              { name: 'Starter', price: '$97', cap: 'Up to 25', features: ['Everything in Free','Match Centre','Chat','Team & Player Stats'], popular: true },
              { name: 'Standard', price: '$197', cap: 'Up to 99', features: ['Everything in Free','Match Centre','Chat','Team & Player Stats'], popular: false },
              { name: 'Pro', price: '$397', cap: 'Up to 400', features: ['Everything in Free','Match Centre','Chat','Team & Player Stats'], popular: false },
            ].map(plan => (
              <div key={plan.name} className={`rounded-2xl border-2 bg-white p-5 text-center ${plan.popular ? 'border-[#5b3cc4] ring-1 ring-[#5b3cc4]/20 relative' : 'border-border'}`}>
                {plan.popular && <Badge className="bg-[#5b3cc4] text-white text-[9px] absolute -top-2.5 left-1/2 -translate-x-1/2">Popular</Badge>}
                <div className="font-heading text-2xl font-bold">{plan.price}</div>
                <div className="text-xs text-muted-foreground">{plan.price === 'Free' ? 'forever' : 'per season'}</div>
                <div className="font-semibold text-sm mt-2">{plan.name}</div>
                <div className="text-xs text-muted-foreground mb-3">{plan.cap} entrants</div>
                <ul className="space-y-1 text-left">
                  {plan.features.map(f => <li key={f} className="text-xs flex items-start gap-1.5"><Check className="h-3 w-3 text-[#5b3cc4] flex-shrink-0 mt-0.5" />{f}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">All plans are per season. Entrants always join for free. Organizer collects their own pool fee separately.</p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">HOW IT WORKS</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Get Started in Seconds</h2>
          </div>
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-full border bg-white p-1 shadow-sm">
              <button className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${howItWorksTab === 'organizer' ? 'bg-[#5b3cc4] text-white shadow' : 'text-muted-foreground'}`} onClick={() => setHowItWorksTab('organizer')}><Trophy className="h-4 w-4" /> Pool Organizer</button>
              <button className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${howItWorksTab === 'entrant' ? 'bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white shadow' : 'text-muted-foreground'}`} onClick={() => setHowItWorksTab('entrant')}><Users className="h-4 w-4" /> Entrant</button>
            </div>
          </div>
          <div className="max-w-2xl mx-auto">
            {howItWorksTab === 'organizer' ? (
              <div className="rounded-2xl border bg-white p-6 sm:p-8">
                <h3 className="font-heading text-xl font-bold mb-1">Create & Run Your Pool</h3>
                <p className="text-sm text-muted-foreground mb-8">Choose your plan, set the rules, crown the champion.</p>
                <div className="space-y-6">
                  {[
                    { step: 1, title: 'Choose Your Plan', desc: 'Free for up to 8 entrants. Paid plans from 47/season unlock Match Centre, chat, and more capacity.', tag: '30 seconds', tagIcon: Clock },
                    { step: 2, title: 'Configure & Invite', desc: 'Set your pool fee, prize structure, and scoring. Share your unique invite link — entrants join for free.', tag: 'Share instantly', tagIcon: Share2 },
                    { step: 3, title: 'Sit Back & Watch', desc: 'Match results sync automatically. Standings update in real-time. You manage prizes offline.', tag: 'Fully automated', tagIcon: Check },
                  ].map(s => (
                    <div key={s.step} className="flex gap-4">
                      <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-[#5b3cc4] flex items-center justify-center"><span className="text-sm font-bold text-white">{s.step}</span></div>
                      <div><h4 className="font-semibold text-sm mb-1">{s.title}</h4><p className="text-sm text-muted-foreground">{s.desc}</p><span className="inline-flex items-center gap-1 text-xs text-[#5b3cc4] font-medium mt-2"><s.tagIcon className="h-3 w-3" />{s.tag}</span></div>
                    </div>
                  ))}
                </div>
                <div className="mt-8"><Button asChild size="lg" className="bg-[#5b3cc4] hover:bg-[#4e33a8]"><Link to="/plans">Create Your Pool <ArrowRight className="h-4 w-4 ml-1" /></Link></Button></div>
              </div>
            ) : (
              <div className="rounded-2xl border bg-white p-6 sm:p-8">
                <h3 className="font-heading text-xl font-bold mb-1">Join & Compete</h3>
                <p className="text-sm text-muted-foreground mb-8">Pick your teams. Track your standing. Win the pool.</p>
                <div className="space-y-6">
                  {[
                    { step: 1, title: 'Sign Up Free', desc: 'Create your account. No credit card, no fees. Entrants always join for free.', tag: '10 seconds', tagIcon: Clock },
                    { step: 2, title: 'Pick Your Teams', desc: '1 team from each of 5 scoring tiers. Predict total season goals. Up to 2 entries per pool.', tag: '5 strategic picks', tagIcon: Target },
                    { step: 3, title: 'Climb the Leaderboard', desc: 'Watch your points grow each matchweek. Chat with pool members. Track prizes.', tag: 'Start competing', tagIcon: Award },
                  ].map(s => (
                    <div key={s.step} className="flex gap-4">
                      <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#a855f7] flex items-center justify-center"><span className="text-sm font-bold text-white">{s.step}</span></div>
                      <div><h4 className="font-semibold text-sm mb-1">{s.title}</h4><p className="text-sm text-muted-foreground">{s.desc}</p><span className="inline-flex items-center gap-1 text-xs text-[#7c3aed] font-medium mt-2"><s.tagIcon className="h-3 w-3" />{s.tag}</span></div>
                    </div>
                  ))}
                </div>
                <div className="mt-8"><Button asChild size="lg" className="bg-gradient-to-r from-[#7c3aed] to-[#a855f7] hover:from-[#6d28d9] hover:to-[#9333ea]"><Link to="/browse">Join a Pool <ArrowRight className="h-4 w-4 ml-1" /></Link></Button></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">FAQ</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Frequently Asked Questions</h2>
          </div>
          <FAQ />
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="bg-gradient-to-br from-[#5b3cc4] via-[#4e33a8] to-[#3b1f8e] py-16 sm:py-20">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 text-center relative z-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Ready to Join the Action?</h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto">Start your own EPL pool or join an existing one. Free for entrants, plans from 47/season for organizers.</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-[#5b3cc4] hover:bg-gray-100 text-base px-8 shadow-lg"><Link to="/plans">Get Started Free <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 text-base px-8"><Link to="/login">Sign In</Link></Button>
            </div>
            <div className="flex justify-center gap-6 text-white/70 text-sm">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-300" /> Free for entrants</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-300" /> 30 second setup</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-300" /> 4 leagues supported</span>
            </div>
          </div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-8">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-base text-muted-foreground hover:opacity-80">
            <img src="/poolsfc-logo.png" alt="Pools FC — Free EPL Pool Management Platform" className="h-12 w-12 rounded-lg" />Pools FC
          </Link>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 mt-4 pt-4 border-t">
          <p className="text-sm text-muted-foreground text-center">Pools FC is an independent platform and is not affiliated with, endorsed by, or connected to the English Premier League, the Football Association, or any of its member clubs.</p>
        </div>
      </footer>
    </div>
  );
}
